import React from "react";
import Table from 'react-bootstrap/Table'
import Container from 'react-bootstrap/Container';
import Card from 'react-bootstrap/Card';
import Spinner from 'react-bootstrap/Spinner'
import Button from 'react-bootstrap/Button';
import ButtonGroup from 'react-bootstrap/ButtonGroup';
import ToggleButton from 'react-bootstrap/ToggleButton';
import Modal from 'react-bootstrap/Modal'
import Tab from 'react-bootstrap/Tab';
import Tabs from 'react-bootstrap/Tabs';
import { useSearchParams  } from "react-router-dom";
import VehicleDetails from "./VehicleDetails/VehicleDetails";
import VehicleTitle from "./VehicleDetails/VehicleTitle";
import { useInventoryContext } from "./InventoryContext";
import { useVehicleContext } from "./VehicleContext";
import { mergeVehicles } from "../../lib/mergeVehicles.js";
import './VehicleCompareList.css';
import { toast } from "react-toastify";
import "./ModalZ.css";

const calcAge = (dt) => {
  if (dt === null)
    return null;
  return Math.floor((new Date().getTime() - new Date(dt).getTime()) / (1000 * 3600 * 24));
}

const vehCard = (v, srcKey) => {
  function cleanValue(v) {
      if (v === "NONE" || v === "UNSPECIFIED" || v === null || v === undefined)
          return "";
      return v.toString().toUpperCase(); // UPPER
  }
  function areValuesSame(v1, v2) {
      // eslint-disable-next-line
      return cleanValue(v1) == cleanValue(v2);
  }  
  function getSpan(key, v, sv) {
    if (!v[key] && !sv[key]) {
      return <span>--</span>
    } else if (areValuesSame(v[key], sv[key]))
      return <span>{sv[key]}</span>;
    else
      return <span className="diff">{sv[key] ?? "[empty]"}</span>;
  }
  function card(key,v,sv) {
    // IOL-specific card format - simplified to just show ID
    if (srcKey === "iol") {
      return <div key={key} className="vehicle-card" title={JSON.stringify(v)}>
        <span className="dim">({sv.id})</span>
      </div>;
    }
    
    // ATC-specific card format - omit invoice price
    if (srcKey === "atc") {
      return <div key={key} className="vehicle-card" title={JSON.stringify(v)}>
        #{getSpan("stock", v, sv)} <span className="dim">({sv.id})</span><br/>
        {getSpan("type",v, sv)} &middot; {getSpan("year",v, sv)} {getSpan("make",v, sv)} {getSpan("model", v, sv)} &middot; {getSpan("trim", v, sv)}<br/>
        <span className="dim">{sv.disposition}</span> &middot; <span className="dim">List </span>${getSpan("listPrice", v, sv)} &middot; <span className="dim">{sv.exteriorColor}</span>
      </div>;
    }
    
    // Default card format for all other sources
    return <div key={key} className="vehicle-card" title={JSON.stringify(v)}>
      #{getSpan("stock", v, sv)} <span className="dim">({sv.id})</span><br/>
      {getSpan("type",v, sv)} &middot; {getSpan("year",v, sv)} {getSpan("make",v, sv)} {getSpan("model", v, sv)} &middot; {getSpan("trim", v, sv)}<br/>
      <span className="dim">{sv.disposition}</span> &middot; <span className="dim">List </span>${getSpan("listPrice", v, sv)} &middot; <span className="dim">Inv </span>${getSpan("invoicePrice", v, sv)} &middot; <span className="dim">{sv.exteriorColor}</span>
    </div>;
  }
  if (v.assoc[srcKey] && v.assoc[srcKey].length > 0)
    return <>{v.assoc[srcKey].map((sv, i) => card(i, v, sv))}</>;
}

function VehicleCompareList(props) {
  const inventoryContext = useInventoryContext();
  const vehicleContext = useVehicleContext();

  const { selectedTab, setSelectedTab } = props;

  const [searchParams, setSearchParams] = useSearchParams();
  const [loadedVehicle, setLoadedVehicle] = React.useState(null);
  const [baseTitle, setBaseTitle] = React.useState(document.title);
  
  const [sortCol, setSortCol] = React.useState("age");
  const [sortDir, setSortDir] = React.useState("asc");

  const [cviChecked, setCviChecked] = React.useState(false);
  const [cvi2Checked, setCvi2Checked] = React.useState(false);
  const [vaChecked, setVaChecked] = React.useState(false);
  const [mvsChecked, setMvsChecked] = React.useState(false);
  const [iolChecked, setIolChecked] = React.useState(false);
  const [ddcChecked, setDdcChecked] = React.useState(false);
  const [vinChecked, setVinChecked] = React.useState(false);
  const [atcChecked, setAtcChecked] = React.useState(false);
  const [vasrcChecked, setVasrcChecked] = React.useState(false);

  const selectedVin = searchParams.get("vin");
  const setSelectedVin = (vin) => {
    if (vin === selectedVin)
      return;
    setSearchParams((prev) => {
      if (vin) {
        prev.set("vin", vin);
      } else {
        prev.delete("vin");
        prev.delete("tab");
      }
      return prev;
    });
  }
    
  const _loadVehicle = async (v) => {
    const getId = (v, key) => { return v.assoc[key] && v.assoc[key].length > 0 ? v.assoc[key][0].id : null; };
    const getRaw = (v, key, name) => { return v.assoc[key] && v.assoc[key].length > 0 ? v.assoc[key][0].raw[name] : null; };
    
    //console.log(v);
    vehicleContext.loadVehicle({
      vin: v.vin,
      cviVehicleId: getId(v, "cvi"),
      // CVI 2.1 details come from the unprojected read by inventory record id; the list row's
      // projection is only what the table needs. `raw` is for sources with no read by id.
      cvi2VehicleId: getId(v, "cvi2"),
      vaVehicleId: getId(v, "va"),
      mvsVehicleId: getId(v, "mvs"),
      iolVehicleId: getId(v, "iol"),
      ddcVehicleId: getId(v, "ddc"),
      vaSrcVehicle: getRaw(v, "vasrc", "Source"),
      vinVehicle: getRaw(v, "vinsol", "Vin"),
      atcVehicleId: getId(v, "atc")
    });
    setLoadedVehicle(v);
  }

  let mergedVehicles = mergeVehicles([
    {key: "cvi", vehicles: inventoryContext.cviSvsInventory.vehicles},
    {key: "cvi2", vehicles: inventoryContext.cvi2Inventory.vehicles},
    {key: "va", vehicles: inventoryContext.vaCoreInventory.vehicles},
    {key: "mvs", vehicles: inventoryContext.vaMvsInventory.vehicles},
    {key: "iol", vehicles: inventoryContext.iolInventory.vehicles},
    {key: "vasrc", vehicles: inventoryContext.cviVaSrcInventory.vehicles},
    {key: "ddc", vehicles: inventoryContext.ddcInventory.vehicles},
    {key: "vinsol", vehicles: inventoryContext.vinInventory.vehicles},
    {key: "atc", vehicles: inventoryContext.atcInventory.vehicles},
  ]);

  let vehicles = mergedVehicles.all.filter((v) => {
    if (v.assoc["cvi"] && v.assoc["cvi"].length > 0 && cviChecked) return true;
    if (v.assoc["cvi2"] && v.assoc["cvi2"].length > 0 && cvi2Checked) return true;
    if (v.assoc["va"] && v.assoc["va"].length > 0 && vaChecked) return true;
    if (v.assoc["mvs"] && v.assoc["mvs"].length > 0 && mvsChecked) return true;
    if (v.assoc["ddc"] && v.assoc["ddc"].length > 0 && ddcChecked) return true;
    if (v.assoc["vinsol"] && v.assoc["vinsol"].length > 0 && vinChecked) return true;
    if (v.assoc["atc"] && v.assoc["atc"].length > 0 && atcChecked) return true;
    if (v.assoc["iol"] && v.assoc["iol"].length > 0 && iolChecked) return true;
    if (v.assoc["vasrc"] && v.assoc["vasrc"].length > 0 && vasrcChecked) return true;
    return false;
  });

  vehicles = vehicles.sort((a, b) => {
    switch (sortCol) {
      case "vin":
        if (a.vin < b.vin) return sortDir === "asc" ? -1 : 1;
        if (a.vin > b.vin) return sortDir === "asc" ? 1 : -1;
        return 0;
      case "age":
        if (a.dateInStock === null) return 1;
        if (b.dateInStock === null) return -1;
        if (a.dateInStock < b.dateInStock) return sortDir === "asc" ? 1 : -1;
        if (a.dateInStock > b.dateInStock) return sortDir === "asc" ? -1 : 1;
        return 0;
      case "cvi":
        if (!a.assoc["cvi"] || a.assoc["cvi"].length === 0) return sortDir === "asc" ? -1 : 1;
        if (!b.assoc["cvi"] || b.assoc["cvi"].length === 0) return sortDir === "asc" ? 1 : -1;
        return 0;
      case "cvi2":
        if (!a.assoc["cvi2"] || a.assoc["cvi2"].length === 0) return sortDir === "asc" ? -1 : 1;
        if (!b.assoc["cvi2"] || b.assoc["cvi2"].length === 0) return sortDir === "asc" ? 1 : -1;
        return 0;
      case "va":
        if (!a.assoc["va"] || a.assoc["va"].length === 0) return sortDir === "asc" ? -1 : 1;
        if (!b.assoc["va"] || b.assoc["va"].length === 0) return sortDir === "asc" ? 1 : -1;
        return 0;
      case "mvs":
        if (!a.assoc["mvs"] || a.assoc["mvs"].length === 0) return sortDir === "asc" ? -1 : 1;
        if (!b.assoc["mvs"] || b.assoc["mvs"].length === 0) return sortDir === "asc" ? 1 : -1;
        return 0;
      case "ddc":
        if (!a.assoc["ddc"] || a.assoc["ddc"].length === 0) return sortDir === "asc" ? -1 : 1;
        if (!b.assoc["ddc"] || b.assoc["ddc"].length === 0) return sortDir === "asc" ? 1 : -1;
        return 0;
      case "vinsol":
        if (!a.assoc["vinsol"] || a.assoc["vinsol"].length === 0) return sortDir === "asc" ? -1 : 1;
        if (!b.assoc["vinsol"] || b.assoc["vinsol"].length === 0) return sortDir === "asc" ? 1 : -1;
        return 0;
      case "atc":
        if (!a.assoc["atc"] || a.assoc["atc"].length === 0) return sortDir === "asc" ? -1 : 1;
        if (!b.assoc["atc"] || b.assoc["atc"].length === 0) return sortDir === "asc" ? 1 : -1;
        return 0;
      case "iol":
        if (!a.assoc["iol"] || a.assoc["iol"].length === 0) return sortDir === "asc" ? -1 : 1;
        if (!b.assoc["iol"] || b.assoc["iol"].length === 0) return sortDir === "asc" ? 1 : -1;
        return 0;
      case "vasrc":
        if (!a.assoc["vasrc"] || a.assoc["vasrc"].length === 0) return sortDir === "asc" ? -1 : 1;
        if (!b.assoc["vasrc"] || b.assoc["vasrc"].length === 0) return sortDir === "asc" ? 1 : -1;
        return 0;
      default:
        return 0;
    }
  });

  function sort(col) {
    if (col === sortCol) {
      setSortDir(sortDir === "asc" ? "desc" : "asc");
    } else {
      setSortCol(col);
      setSortDir("asc");
    }
  }

  function sortIcon(col) {
    if (col !== sortCol) {
      return <></>;
    }
    return sortDir === "asc" ? <>&uarr;</> : <>&darr;</>;
  }

  React.useEffect(() => {
    const loadedVin = loadedVehicle ? loadedVehicle.vin : null;
    //console.log("Load?", selectedVin, loadedVehicle ? loadedVehicle.vin : null, inventoryContext.isReady);
    if (selectedVin && selectedVin !== loadedVin && inventoryContext.isReady) {
       let v = vehicles.find((v) => v.vin === selectedVin);
      if (v) {
        setBaseTitle(document.title);
        document.title = v.vin;
        _loadVehicle(v);
      }
    } else if (!selectedVin && loadedVehicle) {
      document.title = baseTitle;
      setLoadedVehicle(null);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [inventoryContext.isReady, selectedVin, loadedVehicle]);

  React.useEffect(() => {
    setCviChecked(inventoryContext.cviSvsInventory.status === "READY" && inventoryContext.cviSvsInventory.vehicles.length > 0)
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [inventoryContext.cviSvsInventory.status]);

  React.useEffect(() => {
    setCvi2Checked(inventoryContext.cvi2Inventory.status === "READY" && inventoryContext.cvi2Inventory.vehicles.length > 0)
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [inventoryContext.cvi2Inventory.status]);

  React.useEffect(() => {
    setVaChecked(inventoryContext.vaCoreInventory.status === "READY" && inventoryContext.vaCoreInventory.vehicles.length > 0)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [inventoryContext.vaCoreInventory.status]);  

  React.useEffect(() => {
    setMvsChecked(inventoryContext.vaMvsInventory.status === "READY" && inventoryContext.vaMvsInventory.vehicles.length > 0)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [inventoryContext.vaMvsInventory.status]);

  React.useEffect(() => {
    setIolChecked(inventoryContext.iolInventory.status === "READY" && inventoryContext.iolInventory.vehicles.length > 0)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [inventoryContext.iolInventory.status]);

  React.useEffect(() => {
    setDdcChecked(inventoryContext.ddcInventory.status === "READY" && inventoryContext.ddcInventory.vehicles.length > 0)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [inventoryContext.ddcInventory.status]);

  React.useEffect(() => {
    setVinChecked(inventoryContext.vinInventory.status === "READY" && inventoryContext.vinInventory.vehicles.length > 0)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [inventoryContext.vinInventory.status]);

  React.useEffect(() => {
    setAtcChecked(inventoryContext.atcInventory.status === "READY" && inventoryContext.atcInventory.vehicles.length > 0)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [inventoryContext.atcInventory.status]);

  React.useEffect(() => {
    setVasrcChecked(inventoryContext.cviVaSrcInventory.status === "READY" && inventoryContext.cviVaSrcInventory.vehicles.length > 0)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [inventoryContext.cviVaSrcInventory.status]);


  function StatusButton(props) {
    let inventory = props.inventory;
    let label = props.label;
    let checked = props.checked;
    let setChecked = props.setChecked;
    // Toggles key off the label by default. The CVI ones pass an explicit id for the same reason
    // CombinedVehicleList's do: Pendo treats an id of "cvi" as autogenerated and skips it for
    // suggested selection, so they use "cavi"/"cavi2" instead.
    let id = props.id ?? label;

    switch (inventory.status) {
      case "LOADING":
        return <ToggleButton id={id} type="checkbox" variant="outline-secondary" value="1" checked={checked} onChange={(e) => setChecked(e.currentTarget.checked)}>{label} - <Spinner animation="border" size="sm" /></ToggleButton>
      case "READY":
        return <ToggleButton id={id} type="checkbox" variant="outline-success" value="1" checked={checked} onChange={(e) => setChecked(e.currentTarget.checked)}>{label} - <b>{inventory.vehicles.length}</b></ToggleButton>
      case "ERROR":
        return <ToggleButton id={id} type="checkbox" variant="outline-danger" value="1" checked={checked} onChange={(e) => setChecked(e.currentTarget.checked)}>{label} -<b>Error loading</b></ToggleButton>
      case "NONE":
        return <></>;
      default:
        throw new Error("Unknown status: " + inventory.status);
    }
  }

  const handleCopyClick = (e, vin) => {
    e.stopPropagation();
    navigator.clipboard.writeText(vin);
    toast.info(`copied ${vin}`, { autoClose: 1000, theme: 'light' });
  };

  return <div>
    <Container fluid>
      <Card>
        <Tabs activeKey={selectedTab} onSelect={(key) => setSelectedTab(key)} id="inventory-tabs">
          <Tab eventKey="1" title="Summary View">
            {/* Content for Summary View */}
          </Tab>
          <Tab eventKey="2" title="Comparison View">
            {/* Content for Comparison View */}
          </Tab>
        </Tabs>
        <Card.Body className="inventory-results">
        {inventoryContext.isLoading ? <div>
          <ButtonGroup>
          <StatusButton id="cavi" label="CVI 1.0" vehicleSet="cvi" inventory={inventoryContext.cviSvsInventory} checked={cviChecked} setChecked={setCviChecked} />
          <StatusButton id="cavi2" label="CVI 2.1" vehicleSet="cvi2" inventory={inventoryContext.cvi2Inventory} checked={cvi2Checked} setChecked={setCvi2Checked} />
          <StatusButton label="vAuto" vehicleSet="va" inventory={inventoryContext.vaCoreInventory} checked={vaChecked} setChecked={setVaChecked}  />
          <StatusButton label="Merch" vehicleSet="mvs" inventory={inventoryContext.vaMvsInventory} checked={mvsChecked} setChecked={setMvsChecked}  />
          <StatusButton label="IOL" vehicleSet="iol" inventory={inventoryContext.iolInventory} checked={iolChecked} setChecked={setIolChecked}  />
          <StatusButton label="DDC" vehicleSet="ddc" inventory={inventoryContext.ddcInventory} checked={ddcChecked} setChecked={setDdcChecked}  />
          <StatusButton label="VIN" vehicleSet="vin" inventory={inventoryContext.vinInventory} checked={vinChecked} setChecked={setVinChecked}  />
          <StatusButton label="ATC" vehicleSet="atc" inventory={inventoryContext.atcInventory} checked={atcChecked} setChecked={setAtcChecked}  />
          <StatusButton label="vA Source" vehicleSet="vasrc" inventory={inventoryContext.cviVaSrcInventory} checked={vasrcChecked} setChecked={setVasrcChecked}  />
          </ButtonGroup>
          <br/>
          Unique VIN Count: {vehicles.length}s
          </div> : <div>
          <Button disabled={true} variant="secondary">No data loaded</Button>
        </div> }          
        </Card.Body>
      </Card>
    </Container>

    <svg xmlns="http://www.w3.org/2000/svg" style={{display: "none"}} width="16" height="16" fill="currentColor" viewBox="0 0 16 16">
      <defs>
        <symbol id="copy" viewBox="0 0 16 16">
          <path fillRule="evenodd" d="M4 2a2 2 0 0 1 2-2h8a2 2 0 0 1 2 2v8a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2zm2-1a1 1 0 0 0-1 1v8a1 1 0 0 0 1 1h8a1 1 0 0 0 1-1V2a1 1 0 0 0-1-1zM2 5a1 1 0 0 0-1 1v8a1 1 0 0 0 1 1h8a1 1 0 0 0 1-1v-1h1v1a2 2 0 0 1-2 2H2a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2h1v1z"/>
        </symbol>
      </defs>
    </svg>

    {inventoryContext.isReady ? 
    <Table striped bordered hover className="sticky-header-table">
      <thead>
      <tr>
        <th onClick={() => sort('vin')}>{sortIcon('vin')}VIN</th>
        {cviChecked && <th onClick={() => sort('cvi')}>{sortIcon('cvi')}CVI 1.0</th>}
        {cvi2Checked && <th onClick={() => sort('cvi2')}>{sortIcon('cvi2')}CVI 2.1</th>}
        {vaChecked && <th onClick={() => sort('va')}>{sortIcon('va')}vAuto</th>}
        {mvsChecked && <th onClick={() => sort('mvs')}>{sortIcon('mvs')}MVS</th>}
        {ddcChecked && <th onClick={() => sort('ddc')}>{sortIcon('ddc')}DDC</th>}
        {vinChecked && <th onClick={() => sort('vinsol')}>{sortIcon('vinsol')}Vin Solutions</th>}
        {atcChecked && <th onClick={() => sort('atc')}>{sortIcon('atc')}ATC</th>}
        {iolChecked && <th onClick={() => sort('iol')}>{sortIcon('iol')}IOL</th>}
        {vasrcChecked && <th onClick={() => sort('vasrc')}>{sortIcon('vasrc')}VA Source</th>}
        <th onClick={() => sort('age')}>{sortIcon('age')}Age</th>
      </tr>        
      </thead>
      <tbody>
        {vehicles.map((v) =>
        <tr key={v.vin} onClick={() => setSelectedVin(v.vin)} style={{cursor: 'pointer'}}>
          <td>
            {v.vin}  
            <svg onClick={(e) => handleCopyClick(e, v.vin)}><use href="#copy" /></svg>
          </td>
          {cviChecked && <td>{vehCard(v, "cvi")}</td>}
          {cvi2Checked && <td>{vehCard(v, "cvi2")}</td>}
          {vaChecked && <td>{vehCard(v, "va")}</td>}
          {mvsChecked && <td>{vehCard(v, "mvs")}</td>}
          {ddcChecked && <td>{vehCard(v, "ddc")}</td>}
          {vinChecked && <td>{vehCard(v, "vinsol")}</td>}
          {atcChecked && <td>{vehCard(v, "atc")}</td>}
          {iolChecked && <td>{vehCard(v, "iol")}</td>}
          {vasrcChecked && <td>{vehCard(v, "vasrc")}</td>}
          <td>{calcAge(v.dateInStock)}</td>
        </tr>)}
      </tbody>
    </Table>: <div></div>}
{loadedVehicle != null && 
    <Modal centered fullscreen show={loadedVehicle} onHide={() => setSelectedVin(null)}>
      <Modal.Header closeButton>
        <Modal.Title><VehicleTitle /></Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <VehicleDetails />
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={() => setSelectedVin(null)}>Close</Button>
      </Modal.Footer>
    </Modal>}

    </div>;
}

export default VehicleCompareList;
